import React, { useState, useEffect } from 'react';
import { adminService } from '../../api/apiClient';
import { Card, CardHeader, CardTitle, CardBody } from '../../components/UI/Card';
import { StatCard } from '../../components/UI/StatCard';
import { Badge } from '../../components/UI/Badge';
import { Spinner } from '../../components/UI/Spinner';
import {
  Users,
  Receipt,
  TrendingUp,
  CreditCard,
  Server,
} from 'lucide-react';

export const AdminDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAdminDashboard = async () => {
      setLoading(true);
      try {
        const res = await adminService.dashboard();
        if (res && res.data) {
          setData(res.data);
        }
      } catch (err) {
        console.error('Error fetching admin dashboard:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAdminDashboard();
  }, []);

  if (loading) {
    return <Spinner size="lg" label="Cargando métricas globales..." className="py-20" />;
  }

  const stats = data?.stats || {};
  const charts = data?.charts || {};
  const systemInfo = data?.systemInfo || {};

  const revenueMonths = charts.revenueMonths || [];
  const revenueData = charts.revenueData || [];
  const maxRevenue = Math.max(...revenueData, 100);

  return (
    <div className="space-y-6">
      {/* Global Counters Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Usuarios Totales"
          value={stats.totalUsers || 0}
          subtitle={`${stats.activeUsers || 0} activos · ${stats.bannedUsers || 0} suspendidos`}
          icon={Users}
          color="brand"
        />

        <StatCard
          title="Total Facturado Global"
          value={`$${Number(stats.totalInvoicedAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
          subtitle={`${stats.totalInvoices || 0} facturas en total`}
          icon={Receipt}
          color="emerald"
        />

        <StatCard
          title="Total Cobrado Global"
          value={`$${Number(stats.totalCollected || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
          subtitle="Cobros registrados en sistema"
          icon={TrendingUp}
          color="blue"
        />

        <StatCard
          title="Suscripciones Pro"
          value={stats.subscribedUsers || 0}
          subtitle={`$${((stats.subscribedUsers || 0) * 9.99).toFixed(2)} MRR estimado`}
          icon={CreditCard}
          color="amber"
        />
      </div>

      {/* 12-Month Global Revenue Chart */}
      <Card>
        <CardHeader
          actions={
            <Badge variant="primary" size="sm">
              Últimos 12 meses
            </Badge>
          }
        >
          <CardTitle subtitle="Recaudación mensual consolidada de todos los usuarios">
            Facturación Global del Sistema
          </CardTitle>
        </CardHeader>
        <CardBody>
          <div className="h-64 flex items-end justify-between gap-2 sm:gap-4 pt-8 pb-2 px-2 overflow-x-auto">
            {revenueMonths.map((m, idx) => {
              const val = revenueData[idx] || 0;
              const heightPercent = Math.max(8, Math.round((val / maxRevenue) * 100));

              return (
                <div key={m + idx} className="flex-1 flex flex-col items-center gap-2 group h-full justify-end min-w-[32px]">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-slate-900 text-white text-[10px] font-bold py-1 px-2 rounded-md shadow pointer-events-none whitespace-nowrap mb-1">
                    ${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </div>

                  <div className="w-full max-w-[40px] bg-slate-100 dark:bg-slate-800 rounded-t-xl overflow-hidden flex flex-col justify-end h-full">
                    <div
                      style={{ height: `${heightPercent}%` }}
                      className="w-full bg-gradient-to-t from-teal-800 to-[#08707A] group-hover:from-teal-600 group-hover:to-teal-500 rounded-t-xl transition-all duration-300 shadow-sm"
                    />
                  </div>

                  <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-tight">
                    {m.split(' ')[0]}
                  </span>
                </div>
              );
            })}
          </div>
        </CardBody>
      </Card>

      {/* System Diagnostics & Recent Users Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* System Diagnostics */}
        <Card className="p-6 space-y-4">
          <div className="flex items-center gap-2.5">
            <Server className="w-5 h-5 text-[#08707A] dark:text-teal-400" />
            <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">
              Estado del Servidor API
            </h3>
          </div>

          <div className="space-y-3 text-xs">
            <div className="flex justify-between py-1.5 border-b border-slate-100 dark:border-slate-800">
              <span className="text-slate-500">Versión PHP:</span>
              <span className="font-mono font-bold text-slate-800 dark:text-slate-200">{systemInfo.php_version}</span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-slate-100 dark:border-slate-800">
              <span className="text-slate-500">Laravel Framework:</span>
              <span className="font-mono font-bold text-slate-800 dark:text-slate-200">v{systemInfo.laravel_version}</span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-slate-100 dark:border-slate-800">
              <span className="text-slate-500">Entorno:</span>
              <Badge variant="primary" size="sm">{systemInfo.app_env}</Badge>
            </div>
            <div className="flex justify-between py-1.5 border-b border-slate-100 dark:border-slate-800">
              <span className="text-slate-500">Conexión BD:</span>
              <span className="font-mono font-bold text-slate-800 dark:text-slate-200">{systemInfo.db_connection}</span>
            </div>
            <div className="flex justify-between py-1.5">
              <span className="text-slate-500">Hora Servidor:</span>
              <span className="font-mono text-slate-800 dark:text-slate-200 text-[11px]">{systemInfo.server_time}</span>
            </div>
          </div>
        </Card>

        {/* Recent Registrations */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle subtitle="Últimos usuarios registrados en la plataforma">
              Usuarios Recientes
            </CardTitle>
          </CardHeader>
          <CardBody className="p-0">
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {(data?.recentUsers || []).map((u) => (
                <div key={u.id} className="p-4 flex items-center justify-between gap-4 text-xs">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-xl bg-teal-50 dark:bg-teal-950 text-[#08707A] dark:text-teal-400 font-bold flex items-center justify-center text-xs">
                      {u.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <p className="font-bold text-slate-900 dark:text-slate-100">{u.name}</p>
                      <p className="text-[11px] text-slate-400">{u.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {u.is_admin && <Badge variant="admin" size="sm">ADMIN</Badge>}
                    {u.is_subscribed && <Badge variant="success" size="sm">PRO</Badge>}
                    {u.is_banned && <Badge variant="danger" size="sm">BANEADO</Badge>}
                    <span className="text-[10px] text-slate-400">
                      {new Date(u.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};
